import React, { useState } from 'react';
import { GlobeIcon, PlusIcon, SearchIcon, FilterIcon, RefreshCwIcon, KeyIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon, BarChart2Icon, LinkIcon, XIcon, LockIcon, ServerIcon, DatabaseIcon, CreditCardIcon, UsersIcon } from 'lucide-react';
interface OAuth2App {
  id: string;
  name: string;
  logo: string;
  publicKey: string;
  privateKey: string;
  scopes: string[];
  status: 'active' | 'inactive';
  transactionVolume: number;
  transactionCount: number;
  domains: string[];
  connectedDate: string;
  lastUsed: string;
}
interface Scope {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
}
export function Connect() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showPrivateKeys, setShowPrivateKeys] = useState<Record<string, boolean>>({});
  // Toggle visibility of private key for a specific app
  const togglePrivateKeyVisibility = (appId: string) => {
    setShowPrivateKeys(prev => ({
      ...prev,
      [appId]: !prev[appId]
    }));
  };
  // List of available scopes for OAuth2 apps
  const availableScopes: Scope[] = [{
    id: 'transactions',
    name: 'Transactions',
    description: 'Access to view and process transactions',
    icon: <CreditCardIcon size={16} className="text-blue-500" data-id="element-5406" />
  }, {
    id: 'refunds',
    name: 'Refunds',
    description: 'Ability to process and view refunds',
    icon: <RefreshCwIcon size={16} className="text-orange-500" data-id="element-5407" />
  }, {
    id: 'company_info',
    name: 'Company Information',
    description: 'Access to company profile and settings',
    icon: <UsersIcon size={16} className="text-green-500" data-id="element-5408" />
  }, {
    id: 'customer_data',
    name: 'Customer Data',
    description: 'Access to customer information',
    icon: <DatabaseIcon size={16} className="text-purple-500" data-id="element-5409" />
  }, {
    id: 'api_access',
    name: 'API Access',
    description: 'General API access permissions',
    icon: <ServerIcon size={16} className="text-gray-500" data-id="element-5410" />
  }];
  // Mock data for OAuth2 apps
  const oauthApps: OAuth2App[] = [{
    id: 'oauth-001',
    name: 'Shopify',
    logo: 'https://cdn.shopify.com/s/files/1/0533/2089/files/Shopify_logo_small.png?v=1512152678',
    publicKey: 'pub_key_6a8b4c2d3e7f9g0h1i2j',
    privateKey: 'prv_key_5f4e3d2c1b0a9z8y7x6w5v4u3t2s1r',
    scopes: ['transactions', 'refunds', 'company_info'],
    status: 'active',
    transactionVolume: 350000,
    transactionCount: 1243,
    domains: ['mystore.shopify.com', 'checkout.shopify.com'],
    connectedDate: '2023-05-12',
    lastUsed: '2 hours ago'
  }, {
    id: 'oauth-002',
    name: 'Opera PMS',
    logo: 'https://www.oracle.com/a/ocom/img/cb71-opera-cloud-services.jpg',
    publicKey: 'pub_key_7b9c5d3e1f8g2h4i6j0k',
    privateKey: 'prv_key_6g5f4e3d2c1b0a9z8y7x6w5v4u3t',
    scopes: ['transactions', 'company_info'],
    status: 'active',
    transactionVolume: 501600,
    transactionCount: 2156,
    domains: ['operahotel.com', 'api.operacloud.com'],
    connectedDate: '2023-02-28',
    lastUsed: '5 minutes ago'
  }, {
    id: 'oauth-003',
    name: 'CloudBeds',
    logo: 'https://assets.cloudbeds.com/static/images/cloudbeds-logo.svg',
    publicKey: 'pub_key_8c0d6e4f2g9h1i3j5k7l',
    privateKey: 'prv_key_7h6g5f4e3d2c1b0a9z8y7x6w5v',
    scopes: ['transactions', 'refunds', 'customer_data'],
    status: 'active',
    transactionVolume: 263200,
    transactionCount: 954,
    domains: ['app.cloudbeds.com', 'api.cloudbeds.com'],
    connectedDate: '2023-08-15',
    lastUsed: '1 day ago'
  }, {
    id: 'oauth-004',
    name: 'Mews Systems',
    logo: 'https://mews-careers.cdn.prismic.io/mews-careers/a84d4ff0-6b90-44a2-9f7e-c5b511ef17d2_mews-logo.svg',
    publicKey: 'pub_key_9d1e7f5g3h0i2j4k6l8m',
    privateKey: 'prv_key_8i7h6g5f4e3d2c1b0a9z8y7x6w',
    scopes: ['transactions', 'company_info', 'api_access'],
    status: 'inactive',
    transactionVolume: 129600,
    transactionCount: 567,
    domains: ['mews.com', 'api.mews.com'],
    connectedDate: '2023-04-10',
    lastUsed: '3 weeks ago'
  }, {
    id: 'oauth-005',
    name: 'Stripe Connect',
    logo: 'https://stripe.com/img/v3/home/twitter.png',
    publicKey: 'pub_key_0e2f8g6h4i1j3k5l7m9n',
    privateKey: 'prv_key_9j8i7h6g5f4e3d2c1b0a9z8y7x',
    scopes: ['transactions', 'refunds', 'customer_data', 'api_access'],
    status: 'active',
    transactionVolume: 862400,
    transactionCount: 3245,
    domains: ['connect.stripe.com', 'dashboard.stripe.com'],
    connectedDate: '2022-11-20',
    lastUsed: '1 hour ago'
  }, {
    id: 'oauth-006',
    name: 'Salesforce',
    logo: 'https://www.salesforce.com/content/dam/sfdc-docs/www/logos/logo-salesforce.svg',
    publicKey: 'pub_key_1f3g5h7i9j0k2l4m6n8o',
    privateKey: 'prv_key_0k9j8i7h6g5f4e3d2c1b0a9z8y',
    scopes: ['company_info', 'customer_data'],
    status: 'active',
    transactionVolume: 0,
    transactionCount: 0,
    domains: ['salesforce.com', 'force.com'],
    connectedDate: '2023-09-05',
    lastUsed: '3 days ago'
  }, {
    id: 'oauth-007',
    name: 'Booking.com',
    logo: 'https://cf.bstatic.com/static/img/bcom_logo_blue_bg/f12f834e849b2a7f752a14b2598a6ddfeda1e713.svg',
    publicKey: 'pub_key_2g4h6i8j0k1l3m5n7o9p',
    privateKey: 'prv_key_1l0k9j8i7h6g5f4e3d2c1b0a9z',
    scopes: ['transactions', 'refunds', 'company_info'],
    status: 'inactive',
    transactionVolume: 315600,
    transactionCount: 1120,
    domains: ['booking.com', 'admin.booking.com'],
    connectedDate: '2023-01-15',
    lastUsed: '1 month ago'
  }, {
    id: 'oauth-008',
    name: 'Xero Accounting',
    logo: 'https://www.xero.com/content/dam/xero/pilot-images/brand/logos/logo-xero-blue.svg',
    publicKey: 'pub_key_3h5i7j9k1l2m4n6o8p0q',
    privateKey: 'prv_key_2m1l0k9j8i7h6g5f4e3d2c1b0a',
    scopes: ['transactions', 'company_info'],
    status: 'active',
    transactionVolume: 0,
    transactionCount: 0,
    domains: ['xero.com', 'api.xero.com'],
    connectedDate: '2023-07-22',
    lastUsed: '12 hours ago'
  }];
  // Filter apps based on search query and status filter
  const filteredApps = oauthApps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchQuery.toLowerCase()) || app.publicKey.includes(searchQuery) || app.domains.some(domain => domain.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  // Helper function for status badge
  const getStatusBadge = (status: 'active' | 'inactive') => {
    switch (status) {
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-5411">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-5412" />
            Active
          </span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-5413">
            <XCircleIcon size={12} className="mr-1" data-id="element-5414" />
            Inactive
          </span>;
    }
  };
  // Helper function to render scope badges
  const renderScopes = (scopeIds: string[]) => {
    return <div className="flex flex-wrap gap-1" data-id="element-5415">
        {scopeIds.map(scopeId => {
        const scope = availableScopes.find(s => s.id === scopeId);
        if (!scope) return null;
        return <div key={scopeId} className="inline-flex items-center px-2 py-1 bg-gray-100 rounded-md text-xs" title={scope.description} data-id="element-5416">
              {scope.icon}
              <span className="ml-1" data-id="element-5417">{scope.name}</span>
            </div>;
      })}
      </div>;
  };
  return <div className="space-y-6" data-id="element-5418">
      <div className="flex justify-between items-center" data-id="element-5419">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-5420">
          <GlobeIcon size={24} className="mr-2 text-blue-600" data-id="element-5421" />
          Connect OAuth2 Apps
        </h1>
        <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center transition-colors" onClick={() => setShowAddModal(true)} data-id="element-5422">
          <PlusIcon size={16} className="mr-2" data-id="element-5423" />
          Add New Connection
        </button>
      </div>
      {/* Stats overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5424">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-5425">
          <div className="flex items-center" data-id="element-5426">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3" data-id="element-5427">
              <GlobeIcon size={20} className="text-blue-600" data-id="element-5428" />
            </div>
            <div data-id="element-5429">
              <div className="text-sm font-medium text-gray-500" data-id="element-5430">
                Total Connections
              </div>
              <div className="text-2xl font-semibold" data-id="element-5431">{oauthApps.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-5432">
          <div className="flex items-center" data-id="element-5433">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mr-3" data-id="element-5434">
              <CheckCircleIcon size={20} className="text-green-600" data-id="element-5435" />
            </div>
            <div data-id="element-5436">
              <div className="text-sm font-medium text-gray-500" data-id="element-5437">
                Active Connections
              </div>
              <div className="text-2xl font-semibold" data-id="element-5438">
                {oauthApps.filter(app => app.status === 'active').length}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-5439">
          <div className="flex items-center" data-id="element-5440">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mr-3" data-id="element-5441">
              <BarChart2Icon size={20} className="text-purple-600" data-id="element-5442" />
            </div>
            <div data-id="element-5443">
              <div className="text-sm font-medium text-gray-500" data-id="element-5444">
                Total Volume
              </div>
              <div className="text-2xl font-semibold" data-id="element-5445">
                RM
                {oauthApps.reduce((sum, app) => sum + app.transactionVolume, 0).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-5446">
        {/* Filter and search bar */}
        <div className="p-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row gap-4" data-id="element-5447">
          <div className="relative flex-grow" data-id="element-5448">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5449">
              <SearchIcon size={16} className="text-gray-400" data-id="element-5450" />
            </div>
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by app name, API key, or domain..." data-id="element-5451" />
          </div>
          <div className="flex space-x-2" data-id="element-5452">
            <div className="relative" data-id="element-5453">
              <div className="flex items-center" data-id="element-5454">
                <FilterIcon size={16} className="text-gray-500 mr-2" data-id="element-5455" />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block pl-3 pr-8 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md" data-id="element-5456">
                  <option value="all" data-id="element-5457">All Status</option>
                  <option value="active" data-id="element-5458">Active</option>
                  <option value="inactive" data-id="element-5459">Inactive</option>
                </select>
              </div>
            </div>
            <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-5460">
              <RefreshCwIcon size={16} className="mr-2" data-id="element-5461" />
              Refresh
            </button>
          </div>
        </div>
        {/* OAuth Apps grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4" data-id="element-5462">
          {filteredApps.map(app => <div key={app.id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-5463">
              <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-5464">
                <div className="flex items-center" data-id="element-5465">
                  <div className="w-8 h-8 rounded overflow-hidden bg-gray-100 flex items-center justify-center mr-3" data-id="element-5466">
                    <img src={app.logo} alt={`${app.name} logo`} className="max-w-full max-h-full object-contain" onError={e => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `https://ui-avatars.com/api/?name=${app.name}&background=random`;
                }} data-id="element-5467" />
                  </div>
                  <div className="font-medium text-gray-800" data-id="element-5468">{app.name}</div>
                </div>
                {getStatusBadge(app.status)}
              </div>
              <div className="p-4 space-y-3" data-id="element-5469">
                <div className="grid grid-cols-1 gap-3 text-sm" data-id="element-5470">
                  <div className="space-y-1" data-id="element-5471">
                    <div className="text-gray-500 flex items-center" data-id="element-5472">
                      <KeyIcon size={14} className="mr-1 text-gray-400" data-id="element-5473" />
                      Public API Key
                    </div>
                    <div className="flex items-center" data-id="element-5474">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono flex-1 overflow-x-auto" data-id="element-5475">
                        {app.publicKey}
                      </code>
                      <button className="ml-2 text-gray-500 hover:text-gray-700" onClick={() => navigator.clipboard.writeText(app.publicKey)} title="Copy to clipboard" data-id="element-5476">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-5477">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" data-id="element-5478" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1" data-id="element-5479">
                    <div className="text-gray-500 flex items-center" data-id="element-5480">
                      <LockIcon size={14} className="mr-1 text-gray-400" data-id="element-5481" />
                      Private API Key
                    </div>
                    <div className="flex items-center" data-id="element-5482">
                      <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono flex-1 overflow-x-auto" data-id="element-5483">
                        {showPrivateKeys[app.id] ? app.privateKey : '••••••••••••••••••••••••••••••'}
                      </code>
                      <button className="ml-2 text-gray-500 hover:text-gray-700" onClick={() => togglePrivateKeyVisibility(app.id)} title={showPrivateKeys[app.id] ? 'Hide private key' : 'Show private key'} data-id="element-5484">
                        {showPrivateKeys[app.id] ? <EyeOffIcon size={16} data-id="element-5485" /> : <EyeIcon size={16} data-id="element-5486" />}
                      </button>
                      <button className="ml-2 text-gray-500 hover:text-gray-700" onClick={() => navigator.clipboard.writeText(app.privateKey)} title="Copy to clipboard" data-id="element-5487">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-5488">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" data-id="element-5489" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1" data-id="element-5490">
                    <div className="text-gray-500" data-id="element-5491">Authorized Scopes</div>
                    {renderScopes(app.scopes)}
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-5492">
                    <div data-id="element-5493">
                      <div className="text-gray-500" data-id="element-5494">Transaction Volume</div>
                      <div className="font-medium" data-id="element-5495">
                        RM{app.transactionVolume.toLocaleString()}
                      </div>
                    </div>
                    <div data-id="element-5496">
                      <div className="text-gray-500" data-id="element-5497">Transaction Count</div>
                      <div className="font-medium" data-id="element-5498">
                        {app.transactionCount.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-1" data-id="element-5499">
                    <div className="text-gray-500 flex items-center" data-id="element-5500">
                      <LinkIcon size={14} className="mr-1 text-gray-400" data-id="element-5501" />
                      Authorized Domains
                    </div>
                    <div className="flex flex-wrap gap-1" data-id="element-5502">
                      {app.domains.map((domain, index) => <span key={index} className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md" data-id="element-5503">
                          {domain}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200" data-id="element-5504">
                <div className="flex justify-between text-xs text-gray-500" data-id="element-5505">
                  <div data-id="element-5506">Connected: {app.connectedDate}</div>
                  <div data-id="element-5507">Last used: {app.lastUsed}</div>
                </div>
                <div className="flex justify-between mt-2" data-id="element-5508">
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-5509">
                    View Details
                  </button>
                  <button className="text-blue-600 hover:text-blue-800 text-sm font-medium" data-id="element-5510">
                    Manage Scopes
                  </button>
                </div>
              </div>
            </div>)}
        </div>
        {/* Empty state */}
        {filteredApps.length === 0 && <div className="py-12 flex flex-col items-center justify-center text-center" data-id="element-5511">
            <GlobeIcon size={48} className="text-gray-400 mb-4" data-id="element-5512" />
            <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-5513">
              No OAuth2 connections found
            </h3>
            <p className="text-gray-500 max-w-md" data-id="element-5514">
              No connections match your current search criteria. Try adjusting
              your filters or add a new OAuth2 connection.
            </p>
          </div>}
      </div>
      {/* Add New Connection Modal */}
      {showAddModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5515">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-5516">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowAddModal(false)} data-id="element-5517">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-5518"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-5519">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-5520">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-5521">
                <div className="sm:flex sm:items-start" data-id="element-5522">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-5523">
                    <div className="flex justify-between items-center" data-id="element-5524">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-5525">
                        Add New OAuth2 Connection
                      </h3>
                      <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5526">
                        <XIcon size={20} data-id="element-5527" />
                      </button>
                    </div>
                    <div className="mt-4 space-y-4" data-id="element-5528">
                      <div data-id="element-5529">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-5530">
                          App Name
                        </label>
                        <input type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Shopify, Booking.com" data-id="element-5531" />
                      </div>
                      <div data-id="element-5532">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-5533">
                          App Logo URL
                        </label>
                        <input type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="https://example.com/logo.png" data-id="element-5534" />
                      </div>
                      <div data-id="element-5535">
                        <label className="block text-sm font-medium text-gray-700" data-id="element-5536">
                          Authorized Domains
                        </label>
                        <input type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="example.com, api.example.com (comma separated)" data-id="element-5537" />
                      </div>
                      <div data-id="element-5538">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5539">
                          Authorized Scopes
                        </label>
                        <div className="space-y-2" data-id="element-5540">
                          {availableScopes.map(scope => <div key={scope.id} className="flex items-start" data-id="element-5541">
                              <div className="flex items-center h-5" data-id="element-5542">
                                <input id={`scope-${scope.id}`} name={`scope-${scope.id}`} type="checkbox" className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-5543" />
                              </div>
                              <div className="ml-3 text-sm" data-id="element-5544">
                                <label htmlFor={`scope-${scope.id}`} className="font-medium text-gray-700 flex items-center" data-id="element-5545">
                                  {scope.icon}
                                  <span className="ml-1" data-id="element-5546">{scope.name}</span>
                                </label>
                                <p className="text-gray-500" data-id="element-5547">
                                  {scope.description}
                                </p>
                              </div>
                            </div>)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-5548">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowAddModal(false)} data-id="element-5549">
                  Create Connection
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowAddModal(false)} data-id="element-5550">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}